import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc } from '@if/core/date'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material'
import {
  useMaxPaymentAmountWithAgentLeeway,
  useMaxPaymentError,
} from '@src/containers/ViewWorksheet/viewWorksheet-hooks'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import availableCreditEffects from '@src/data/store/AvailableCredit/available-credit-effects'
import { availableCreditSelectors } from '@src/data/store/AvailableCredit/available-credit-store'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { debounce } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Breadcrumb, InputTextField, PageError, PageSpinner, SelectComponent } from '../../../components'
import { TRootState, useAppDispatch, useAppSelector } from '../../../data/store'
import { appActions, appSelectors } from '../../../data/store/AppStore'
import { b2cWorksheetEffects } from '../../../data/store/B2cWorksheet'
import { b2cWorksheetActions, b2cWorksheetSelectors } from '../../../data/store/B2cWorksheet/b2c-worksheet-store'
import { creditSelectors } from '../../../data/store/CreditApplication'
import { fullCreditApplicationEffects } from '../../../data/store/FullCreditApplication'
import { Constants, SelectValueListItem } from '../../../data/types'
import { EditB2cWorksheetDto, EditB2cWorksheetDtoSchema } from '../../../data/types/B2cWorksheetSchema'
import { FundingInfoDto } from '../../../data/types/FundingInfoDto'
import { reportErrorToConsole } from '../../../services/error-logger'
import MerchantPaymentInstructions from '../../ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import {
  aprThresholds,
  useAprTooHigh,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'

const EditB2cWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const creditApplication = useAppSelector(creditSelectors.getCurrent)

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<EditB2cWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditB2cWorksheetDto,
    resolver: yupResolver(EditB2cWorksheetDtoSchema),
  })

  const dispatch = useAppDispatch()
  const dispatchEffect = useSideEffect()
  const availableCredit = useAppSelector(availableCreditSelectors.getAvailableCredit)
  const creditInProgress = useAppSelector(availableCreditSelectors.getCreditInProgress)
  const isLoading = useAppSelector(b2cWorksheetSelectors.isSavingB2cWorksheet)
  const isLoadingCreditApplication = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const isComputingFunding = useAppSelector(b2cWorksheetSelectors.isComputeB2cFunding)
  const error = useAppSelector(appSelectors.getBusinessError)
  const merchant = useAppSelector(creditSelectors.getMerchant)
  const b2cWorksheet = useAppSelector(b2cWorksheetSelectors.getCurrent)
  const computedInfo = useAppSelector(b2cWorksheetSelectors.getComputedInfo)
  const totalFees = useAppSelector(b2cWorksheetSelectors.getTotalfees)
  const totalInsurance = useAppSelector(b2cWorksheetSelectors.getTotalInsurance)
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    [],
    new Set<string>(),
    b2cWorksheet?.paymentPlanId,
  )

  const programFinancingConfig = useAppSelector((state: TRootState) =>
    appSelectors.getFinancingConfig(state, creditApplication?.financingProgramId),
  )
  const [merchantFinancingConfig] = useMerchantFinancingConfig(merchant?.id, creditApplication?.financingProgramId)
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  const possibleTerms = usePossibleTerms(creditApplication?.finalCreditDecision.maxTermDuration, false, financingConfig)

  const provinceConfigs = useAppSelector(appSelectors.getProvinceConfigurations)

  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication)

  const firstPaymentOn = watch('firstPaymentOn')

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  // dynamic values
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const paymentFrequency = watch('paymentFrequency')
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')

  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland
  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )
  const totalObligation = amountRequested + (computedInfo?.totalInterestAmount ?? 0) + totalFees + totalInsurance
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default
  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    paymentPlanId,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )
  const maxPaymentForFrequencyError = useMaxPaymentError(
    b2cWorksheet?.paymentFrequency,
    computedInfo?.paymentForFrequency,
    maxPaymentAmount,
    dispatch,
    Constants.financingProgramConfigs[EFinancingProgram.B2c].validateMaxPaymentAmountInWorksheet,
  )
  useEffect(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        availableCreditEffects.getAvailableCredit(creditApplication.id, creditApplication.financingProgramId),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication, dispatchEffect])

  // reset form on worksheet changed
  useEffect(() => {
    if (b2cWorksheet) {
      reset(b2cWorksheet as EditB2cWorksheetDto)
      setValue('firstPaymentOn', b2cWorksheet.firstPaymentOn ?? new Date())
    }
  }, [b2cWorksheet, merchant, reset, setValue])

  const computeFunding = useCallback(
    (computeDto: FundingInfoDto) => {
      return dispatchEffect(b2cWorksheetEffects.computeB2cFunding(computeDto))
    },
    [dispatchEffect],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(computeFunding, 500), [computeFunding])

  // recompute funding
  useEffect(() => {
    if (creditApplication && id && merchant && b2cWorksheet) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const stateIso = creditApplication.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant: false,
        stateIso,
        deliveryOn,
        firstPaymentOn,
        requestId,
        creditApplicationId: id,
        merchantId: merchant.id,
        insuranceCompanyId: b2cWorksheet.insuranceCompanyId,
      }
      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        debounceComputeFunding(computeDto)?.catch(reportErrorToConsole)
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        dispatch(b2cWorksheetActions.setFundingComputed(emptyComputedDto))
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    b2cWorksheet,
    debounceComputeFunding,
    dispatch,
    id,
    merchant?.id,
    merchant,
    financingConfig.minimumLoanAmount,
  ])

  const { firstPaymentDateOptions } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: b2cWorksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  const onFormSubmitted = (data: EditB2cWorksheetDto) => {
    const expiry = new Date(creditApplication!.expiresOn)
    const receivedOnPlus90Days = new Date(creditApplication!.applicant.hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    if (
      isNFl
        ? computedInfo.effectiveRate > highCostRateForProvince
        : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
    ) {
      dispatch(
        appActions.setBusinessErrors([
          {
            message: t('worksheetCommon.effectiveRateTooHigh', '', {
              effectiveRate: computedInfo.effectiveRate,
              maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
            }),
          },
        ]),
      )
    } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }]))
    } else if (amountRequested > availableCredit) {
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }]))
    } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }]))
    else if (compareAsc(deliveryOnUTC, expiry) === 1)
      dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }]))
    else {
      data.amountRequested = amountRequested
      data.status = EWorksheetStatus.Active
      dispatchEffect(b2cWorksheetEffects.update(data))
        .then(() =>
          dispatchEffect(fullCreditApplicationEffects.getById(id!, EFinancingProgram.B2c)).then(() =>
            navigate(`/Applications/${EFinancingProgram.B2c}/${id}/view`, { replace: true }),
          ),
        )
        .catch(reportErrorToConsole)
    }
  }

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.B2c}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isLoading && isLoadingCreditApplication} />
      {!isLoading && (
        <Stack>
          <PageError errors={error} />
          {creditApplication && aprTooHigh && (
            <Alert severity="error">
              {t('common.errors.aprTooHigh', '', {
                threshold: aprThresholds[creditApplication.applicant.currentAddress.stateIso],
                stateIso: t(`EProvince.${creditApplication.applicant.currentAddress.stateIso}`, { ns: 'enum' }),
              })}
            </Alert>
          )}
        </Stack>
      )}
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField value={`${planName}`} label={t('worksheetCommon.program')} disabled />
          <InputTextField
            label={t('worksheetCommon.creditLimit')}
            disabled
            value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
          />
          <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
          <div style={{ marginTop: '1%' }}>
            <Button
              variant="contained"
              type="submit"
              disabled={
                aprTooHigh || isComputingFunding || !computedInfo || isLoading || maxPaymentForFrequencyError !== null
              }
            >
              {t('common.save')}
            </Button>
          </div>
        </Stack>
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={firstPaymentDateOptions.map((item) => {
                    const data: SelectValueListItem = {
                      label: formatDate(item),
                      value: formatDate(item),
                    }
                    return data
                  })}
                  label={t('worksheetCommon.firstPaymentDate')}
                  {...register('firstPaymentOn')}
                  error={errors?.firstPaymentOn}
                  value={formatDate(firstPaymentOn)}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={possibleTerms.map((item) => {
                    const data: SelectValueListItem = {
                      label: `${item} ${t('worksheet.month')}`,
                      value: item,
                    }
                    return data
                  })}
                  label={t('worksheet.term')}
                  {...register('term')}
                  error={errors?.term}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={b2cWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('worksheetCommon.fundingSummary')} :
                </Typography>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                  <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>
                    {`${t('worksheetCommon.interest')} (${`${creditApplication?.finalCreditDecision?.interestRate}%`})`}
                    :{' '}
                  </Typography>
                  <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                  <Typography>{formatCurrency(totalFees)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                  <Typography>{formatCurrency(totalInsurance)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                  <Typography>{formatCurrency(totalObligation)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                  <Typography>{computedInfo.effectiveRate} %</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.payment')}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                  <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                  <Typography>
                    {b2cWorksheet?.paymentFrequency
                      ? t(`EPaymentFrequency.${b2cWorksheet?.paymentFrequency}`, { ns: 'enum' })
                      : t('EPaymentFrequency.monthly', { ns: 'enum' })}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                  <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                  <Typography>{formatDate(firstPaymentOn)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                  <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}

export default EditB2cWorksheetPage
